import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { useSelector } from 'react-redux';
import {
    Table, TableBody, Typography, TableContainer, TableHead, TableRow, Paper,
    Box, Chip, Button, CircularProgress, Alert, Dialog, DialogTitle,
    DialogContent, DialogActions, IconButton, Tooltip, useMediaQuery, useTheme,
    Card, CardContent, Stack, Divider
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ImageIcon from '@mui/icons-material/Image';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import PatientImageViewer from '../../components/PatientImageViewer';

const serverApiUrl = process.env.REACT_APP_SERVER_API || 'http://localhost:9000/';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const tableHeading = ["Exam Date", "Exams", "Radiologist", "Report Date", "Actions"];

// Mobile Report Card Component
const ReportCard = ({ report, formatDate, onViewReport, onOpenImages, canViewImages }) => (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
        <CardContent sx={{ pb: 2 }}>
            <Stack spacing={1.5}>
                {/* Date and Radiologist */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarTodayIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight={600}>
                            {formatDate(report.appointmentDate)}
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Report: {formatDate(report.reportDate)}
                    </Typography>
                </Box>

                {/* Exams */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {report.examNames?.length > 0 ? (
                        report.examNames.map((exam, idx) => (
                            <Chip
                                key={idx}
                                label={exam}
                                size="small"
                                variant="outlined"
                                color="primary"
                            />
                        ))
                    ) : (
                        <Chip label="Imaging Study" size="small" variant="outlined" />
                    )}
                </Box>

                {/* Radiologist */}
                {report.radiologistName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                            {report.radiologistName}
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ my: 0.5 }} />

                {/* Actions */}
                <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => onViewReport(report)}
                        startIcon={<PictureAsPdfIcon />}
                        size="medium"
                    >
                        View Report
                    </Button>
                    {canViewImages && report.orthancStudyInstanceUID && (
                        <Button
                            fullWidth
                            variant="outlined"
                            color="primary"
                            onClick={() => onOpenImages(report.orthancStudyInstanceUID)}
                            startIcon={<ImageIcon />}
                            size="medium"
                        >
                            Images
                        </Button>
                    )}
                </Stack>
            </Stack>
        </CardContent>
    </Card>
);

export default function MyReports() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { userData } = useSelector((state) => state.auth);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pdfDialog, setPdfDialog] = useState({ open: false, pdfUrl: null, loading: false, report: null });

    const canViewImages = userData?.canViewImages || false;
    const [imageViewer, setImageViewer] = useState({ open: false, studyInstanceUID: null });

    useEffect(() => {
        fetchReports();
    }, []);

    // Cleanup PDF URL when dialog closes
    useEffect(() => {
        return () => {
            if (pdfDialog.pdfUrl) {
                window.URL.revokeObjectURL(pdfDialog.pdfUrl);
            }
        };
    }, [pdfDialog.pdfUrl]);

    const fetchReports = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('item');
            const response = await fetch(`${serverApiUrl}api/patient/my-reports`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            if (data.success) {
                setReports(data.data || []);
            } else {
                setError(data.message || 'Failed to fetch reports');
            }
        } catch (err) {
            console.error('Error fetching reports:', err);
            setError('Failed to fetch reports. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const handleOpenImages = (studyInstanceUID) => {
        if (studyInstanceUID) {
            setImageViewer({ open: true, studyInstanceUID });
        }
    };

    const handleCloseImageViewer = () => {
        setImageViewer({ open: false, studyInstanceUID: null });
    };

    const handleViewReport = async (report) => {
        try {
            setPdfDialog({ open: true, pdfUrl: null, loading: true, report });
            const token = localStorage.getItem('item');

            const response = await fetch(`${serverApiUrl}api/generateReportPDF`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    patientId: report.patientId,
                    reportId: report.reportId,
                    studyImages: []
                })
            });

            if (!response.ok) {
                throw new Error('Failed to generate PDF');
            }

            const blob = await response.blob();
            const pdfUrl = window.URL.createObjectURL(blob);
            setPdfDialog(prev => ({ ...prev, pdfUrl, loading: false }));
        } catch (err) {
            console.error('Error loading PDF:', err);
            alert('Failed to load report. Please try again.');
            setPdfDialog({ open: false, pdfUrl: null, loading: false, report: null });
        }
    };

    const handleClosePdfDialog = () => {
        if (pdfDialog.pdfUrl) {
            window.URL.revokeObjectURL(pdfDialog.pdfUrl);
        }
        setPdfDialog({ open: false, pdfUrl: null, loading: false, report: null });
    };

    const handleDownloadPdf = () => {
        if (pdfDialog.pdfUrl && pdfDialog.report) {
            const a = document.createElement('a');
            a.href = pdfDialog.pdfUrl;
            a.download = `Report_${pdfDialog.report.orderId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    };

    const handlePrintPdf = () => {
        if (pdfDialog.pdfUrl) {
            const printWindow = window.open(pdfDialog.pdfUrl, '_blank');
            if (printWindow) {
                printWindow.addEventListener('load', () => {
                    printWindow.print();
                });
            }
        }
    };

    // On mobile, open PDF directly in new tab (better UX than iframe)
    const handleMobileViewReport = async (report) => {
        try {
            setPdfDialog({ open: false, pdfUrl: null, loading: true, report });
            const token = localStorage.getItem('item');

            const response = await fetch(`${serverApiUrl}api/generateReportPDF`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    patientId: report.patientId,
                    reportId: report.reportId,
                    studyImages: []
                })
            });

            if (!response.ok) {
                throw new Error('Failed to generate PDF');
            }

            const blob = await response.blob();
            const pdfUrl = window.URL.createObjectURL(blob);

            // On mobile, open in new tab for better PDF viewing experience
            window.open(pdfUrl, '_blank');

            // Cleanup after a delay
            setTimeout(() => {
                window.URL.revokeObjectURL(pdfUrl);
            }, 60000);

            setPdfDialog({ open: false, pdfUrl: null, loading: false, report: null });
        } catch (err) {
            console.error('Error loading PDF:', err);
            alert('Failed to load report. Please try again.');
            setPdfDialog({ open: false, pdfUrl: null, loading: false, report: null });
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                My Reports
            </Typography>

            {reports.length === 0 ? (
                <Alert severity="info">
                    No completed reports available yet. Reports will appear here once your imaging studies have been reviewed.
                </Alert>
            ) : isMobile ? (
                // Mobile Card Layout
                <Box>
                    {reports.map(report => (
                        <ReportCard
                            key={report.reportId}
                            report={report}
                            formatDate={formatDate}
                            onViewReport={isSmallMobile ? handleMobileViewReport : handleViewReport}
                            onOpenImages={handleOpenImages}
                            canViewImages={canViewImages}
                        />
                    ))}
                </Box>
            ) : (
                // Desktop Table Layout
                <Box sx={{ overflowX: 'auto' }}>
                    <TableContainer component={Paper} className='tableShadow'>
                        <Table sx={{ minWidth: 700 }} aria-label="my reports table" className='commonTable'>
                            <TableHead>
                                <TableRow>
                                    {tableHeading.map(elem => (
                                        <StyledTableCell key={elem}>{elem}</StyledTableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reports.map(report => (
                                    <StyledTableRow key={report.reportId}>
                                        <StyledTableCell>
                                            {formatDate(report.appointmentDate)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {report.examNames?.length > 0 ? (
                                                    report.examNames.slice(0, 2).map((exam, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={exam}
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                        />
                                                    ))
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Imaging Study
                                                    </Typography>
                                                )}
                                                {report.examNames?.length > 2 && (
                                                    <Chip
                                                        label={`+${report.examNames.length - 2}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {report.radiologistName || 'N/A'}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {formatDate(report.reportDate)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Tooltip title="View Report">
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => handleViewReport(report)}
                                                        startIcon={<PictureAsPdfIcon />}
                                                    >
                                                        View Report
                                                    </Button>
                                                </Tooltip>

                                                {canViewImages && report.orthancStudyInstanceUID && (
                                                    <Tooltip title="Open Images in Viewer">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() => handleOpenImages(report.orthancStudyInstanceUID)}
                                                            startIcon={<ImageIcon />}
                                                        >
                                                            Images
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Loading Overlay for Mobile */}
            {pdfDialog.loading && isSmallMobile && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(255,255,255,0.9)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <CircularProgress size={48} />
                    <Typography sx={{ mt: 2 }}>Loading report...</Typography>
                </Box>
            )}

            {/* PDF Viewer Dialog - Tablet and Desktop */}
            <Dialog
                open={pdfDialog.open && !isSmallMobile}
                onClose={handleClosePdfDialog}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        height: isMobile ? '100%' : '90vh',
                        maxHeight: isMobile ? '100%' : '90vh',
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                    py: 1,
                    px: { xs: 1.5, sm: 2 },
                    minHeight: { xs: 56, sm: 64 }
                }}>
                    <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600} noWrap sx={{ flex: 1, mr: 1 }}>
                        {pdfDialog.report ? `Report - ${formatDate(pdfDialog.report.appointmentDate)}` : 'Report'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center' }}>
                        <Tooltip title="Download PDF">
                            <IconButton
                                onClick={handleDownloadPdf}
                                disabled={!pdfDialog.pdfUrl}
                                color="primary"
                                size={isMobile ? "medium" : "large"}
                            >
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Print">
                            <IconButton
                                onClick={handlePrintPdf}
                                disabled={!pdfDialog.pdfUrl}
                                color="primary"
                                size={isMobile ? "medium" : "large"}
                            >
                                <PrintIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={handleClosePdfDialog} size={isMobile ? "medium" : "large"}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {pdfDialog.loading ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flex: 1,
                            minHeight: 300
                        }}>
                            <CircularProgress size={48} />
                            <Typography sx={{ mt: 2 }}>Loading report...</Typography>
                        </Box>
                    ) : pdfDialog.pdfUrl ? (
                        <iframe
                            src={pdfDialog.pdfUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                flex: 1
                            }}
                            title="Report PDF"
                        />
                    ) : null}
                </DialogContent>
                <DialogActions sx={{
                    borderTop: 1,
                    borderColor: 'divider',
                    px: { xs: 1.5, sm: 2 },
                    py: 1.5,
                    flexWrap: 'wrap',
                    gap: 1
                }}>
                    <Button
                        onClick={handleDownloadPdf}
                        disabled={!pdfDialog.pdfUrl}
                        startIcon={<DownloadIcon />}
                        variant="outlined"
                        size={isMobile ? "medium" : "large"}
                        sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 120 } }}
                    >
                        Download
                    </Button>
                    <Button
                        onClick={handlePrintPdf}
                        disabled={!pdfDialog.pdfUrl}
                        startIcon={<PrintIcon />}
                        variant="outlined"
                        size={isMobile ? "medium" : "large"}
                        sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 100 } }}
                    >
                        Print
                    </Button>
                    <Button
                        onClick={handleClosePdfDialog}
                        variant="contained"
                        size={isMobile ? "medium" : "large"}
                        sx={{ flex: { xs: '1 1 100%', sm: 'none' }, minWidth: { xs: 0, sm: 100 } }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Viewer */}
            <PatientImageViewer
                open={imageViewer.open}
                onClose={handleCloseImageViewer}
                studyInstanceUID={imageViewer.studyInstanceUID}
            />
        </>
    );
}
